import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  GetUsersDocument,
  IGetUsersQuery,
  IUser,
  useDeleteUserMutation,
  useGetUsersQuery
} from '../../../generated/graphql';
import { Roles, Categories } from '../../../enums/RoleEnums';
import { buttons, Colors } from '../../../utils/theme';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Person, PeopleAlt } from '@mui/icons-material';
import { Button, Divider, Grid, Typography } from '@mui/material';
import OverviewTable from '../../Common/Tables/OverviewTable';
import Loader from '../../Common/Loader';
import { useNavigate } from 'react-router-dom';
import OverviewToolbar from '../../Common/Layout/OverviewToolbar';
import CreateUser from './CreateUser';

export const colorings = (category: string): string => {
  switch (category) {
    case Categories.Comminications:
      return Colors.warning;
    case Categories.Research:
      return Colors.secondary;
    default:
      return '';
  }
};

export const coloring = (role: string): string => {
  switch (role) {
    case Roles.Admin:
      return Colors.warning;
    case Roles.InvoiceEditor:
      return Colors.main;
    case Roles.Operator:
      return Colors.third;
    case Roles.EsgOperator:
      return Colors.pink;
    case Roles.IndexOperator:
      return Colors.dark_green;
    case Roles.MarketingOperator:
      return Colors.purple;
    default:
      return '';
  }
};

export const makeFirstLetterCapital = (takesRole: string | null): string => {
  if (takesRole) {
    const capitalized = takesRole.charAt(0).toUpperCase() + takesRole.slice(1);
    return capitalized.replace('_', ' ');
  }
  return '';
};

interface IFilter {
  operator: boolean;
  admin: boolean;
  esgOperator: boolean;
  invoiceEditor: boolean;
  indexOperator: boolean;
  marketingOperator: boolean;
}

const initialFilter: IFilter = {
  operator: false,
  admin: false,
  esgOperator: false,
  invoiceEditor: false,
  indexOperator: false,
  marketingOperator: false
};

const Users: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { dispatch } = useContext(AppContext);
  const { data, loading } = useGetUsersQuery();
  const [users, setUsers] = useState<IUser[]>([]);
  const [operator, setOperator] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [esgOperator, setEsgOperator] = useState(false);
  const [invoiceEditor, setInvoiceEditor] = useState(false);
  const [indexOperator, setIndexOperator] = useState(false);
  const [marketingOperator, setMarketingOperator] = useState(false);
  const [filter, setFilter] = useState(initialFilter);
  const [allUsers, setAllUsers] = useState<IUser[]>([]);
  const [openCreateUserForm, setOpenCreateUserForm] = useState(false);
  const [deleteUser] = useDeleteUserMutation();

  useEffect(() => {
    if (data && data.result) {
      setUsers(data.result as IUser[]);
      setAllUsers(data.result as IUser[]);
    }
  }, [data]);

  useEffect(() => {
    filterUsers(filter);
  }, [allUsers]);

  const filterUsers = (filter: IFilter) => {
    let filteredUsers = allUsers;
    if (filter.operator) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.Operator));
    }
    if (filter.admin) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.Admin));
    }
    if (filter.esgOperator) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.EsgOperator));
    }
    if (filter.invoiceEditor) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.InvoiceEditor));
    }
    if (filter.indexOperator) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.IndexOperator));
    }
    if (filter.marketingOperator) {
      filteredUsers = filteredUsers.filter((user) => user.roles.includes(Roles.MarketingOperator));
    }
    setFilter(filter);
    setUsers(filteredUsers);
  };

  const handleOperatorPressed = () => {
    const newFilter = { ...filter, operator: !operator };
    setOperator(!operator);
    filterUsers(newFilter);
  };

  const handleAdminPressed = () => {
    const newFilter = { ...filter, admin: !admin };
    setAdmin(!admin);
    filterUsers(newFilter);
  };

  const handleEsgOperatorPressed = () => {
    const newFilter = { ...filter, esgOperator: !esgOperator };
    setEsgOperator(!esgOperator);
    filterUsers(newFilter);
  };

  const handleInvoiceEditorPressed = () => {
    const newFilter = { ...filter, invoiceEditor: !invoiceEditor };
    setInvoiceEditor(!invoiceEditor);
    filterUsers(newFilter);
  };

  const handleIndexOperatorPressed = () => {
    const newFilter = { ...filter, indexOperator: !indexOperator };
    setIndexOperator(!indexOperator);
    filterUsers(newFilter);
  };

  const handleMarketingOperatorPressed = () => {
    const newFilter = { ...filter, marketingOperator: !marketingOperator };
    setMarketingOperator(!marketingOperator);
    filterUsers(newFilter);
  };

  const renderUsername = (rowData: IUser) => {
    return (
      <span style={{ fontSize: 14 }}>
        <Person /> {rowData.username}
      </span>
    );
  };

  const remove = (rowData: IUser) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.username}?`,
        agreeAction: handleDelete,
        params: [rowData.id, rowData]
      }
    });
  };

  const handleDelete = (params: string[]) => {
    const id = params[0];
    const rowData = params[1] as unknown as IUser;
    if (!id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteUser({
      variables: {
        id: parseInt(id)
      },
      update(cache) {
        const usersQuery = cache.readQuery({ query: GetUsersDocument }) as IGetUsersQuery;
        if (usersQuery.result) {
          const cacheUserIndex = usersQuery.result.findIndex((u) => u?.id === id);
          if (cacheUserIndex > -1) {
            const data = usersQuery.result.filter(function (el) {
              return el?.id !== id;
            });
            cache.writeQuery({
              query: GetUsersDocument,
              data: { result: data }
            });
          }
        }
        const rowDataUsername =
          rowData.username.charAt(0).toUpperCase() + rowData.username.slice(1);
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `User ${rowDataUsername} deleted.`, severity: 'success' }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const renderUserGroups = (rowData: IUser) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {rowData.roles.map((role, index) => (
          <Box key={index} style={{ marginRight: 25 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <PeopleAlt
                style={{
                  color: coloring(role as string),
                  marginRight: 10
                }}
              />
              <Typography
                style={{
                  fontSize: 14,
                  fontFamily: 'Overpass',
                  color: coloring(role as string),
                  marginTop: 5,
                  marginRight: 25
                }}
              >
                {makeFirstLetterCapital(role)}
              </Typography>
              {index + 1 < rowData.roles.length && (
                <Divider orientation="vertical" flexItem sx={{ background: '#B4D5F9' }} />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const columns = [
    { title: 'Details', width: 83, sorting: false },
    { title: 'Username', field: 'username', render: renderUsername, width: 507 },
    { title: 'User Groups', field: 'roles', render: renderUserGroups, width: 992 },
    { title: 'Remove', width: 140, sorting: false }
  ];

  const handleDetails = (rowData: IUser) => {
    dispatch({
      type: Actions.SetSelectedUser,
      payload: {
        user: rowData
      }
    });
    navigateTo(rowData);
  };

  const navigateTo = (rowData: IUser) => {
    navigate(`/users/${rowData.id}`);
  };

  return (
    <Box className="overviewPage" sx={{ height: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <OverviewToolbar>
          <Person sx={{ marginRight: '7px' }} /> Users
        </OverviewToolbar>
      </Box>
      <Box>
        <OverviewTable
          columns={columns}
          data={users}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={remove}
          pageSize={15}
          createButtonProps={{
            id: 'create-user-button',
            onClick: () => setOpenCreateUserForm(true),
            text: 'Create User',
            disabled: false
          }}
          additionalFilters={
            <Box sx={{ marginLeft: '40px', marginRight: '226px' }}>
              <Button
                id="operator-filter-button"
                data-testid="operator-filter-button"
                variant="contained"
                sx={operator ? buttons.selected : buttons.deSelected}
                color="primary"
                name="operator button"
                onClick={handleOperatorPressed}
              >
                OPERATOR
              </Button>
              <Button
                id="admin-filter-button"
                data-testid="admin-filter-button"
                variant="contained"
                sx={admin ? buttons.selected : buttons.deSelected}
                color="primary"
                name="admin button"
                onClick={handleAdminPressed}
              >
                ADMIN
              </Button>
              <Button
                id="esg-operator-filter-button"
                data-testid="esg-operator-filter-button"
                variant="contained"
                sx={esgOperator ? buttons.selected : buttons.deSelected}
                color="primary"
                name="esg operator button"
                onClick={handleEsgOperatorPressed}
              >
                ESG OPERATOR
              </Button>
              <Button
                id="invoice-editor-filter-button"
                data-testid="invoice-editor-filter-button"
                variant="contained"
                sx={invoiceEditor ? buttons.selected : buttons.deSelected}
                color="primary"
                name="invoice editor button"
                onClick={handleInvoiceEditorPressed}
              >
                INVOICE EDITOR
              </Button>
              <Button
                id="index-operator-filter-button"
                data-testid="index-operator-filter-button"
                variant="contained"
                sx={indexOperator ? buttons.selected : buttons.deSelected}
                color="primary"
                name="index operator button"
                onClick={handleIndexOperatorPressed}
              >
                INDEX OPERATOR
              </Button>
              <Button
                id="marketing-operator-filter-button"
                data-testid="marketing-operator-filter-button"
                variant="contained"
                sx={marketingOperator ? buttons.selected : buttons.deSelected}
                color="primary"
                name="marketing operator button"
                onClick={handleMarketingOperatorPressed}
              >
                MARKETING OPERATOR
              </Button>
            </Box>
          }
        />
        {openCreateUserForm && <CreateUser handleClose={() => setOpenCreateUserForm(false)} />}
        <Loader loading={loading} />
      </Box>
    </Box>
  );
};

export default Users;
